import type { Preferences } from "@atrium/shared";

/** Browser notification permission, or "unsupported" where the API is missing. */
export type NotifyPermission = NotificationPermission | "unsupported";

export function notifyPermission(): NotifyPermission {
  return typeof window !== "undefined" && "Notification" in window ? Notification.permission : "unsupported";
}

export async function requestNotifyPermission(): Promise<NotifyPermission> {
  if (notifyPermission() === "unsupported") return "unsupported";
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};

/** True when `now` falls inside the quiet-hours window (which may wrap past midnight). */
export function inQuietHours(q: Preferences["quietHours"], now = new Date()): boolean {
  if (!q.enabled) return false;
  const start = toMinutes(q.start);
  const end = toMinutes(q.end);
  if (start === end) return false;
  const t = now.getHours() * 60 + now.getMinutes();
  return start < end ? t >= start && t < end : t >= start || t < end;
}

let ctx: AudioContext | null = null;

/** A short, quiet two-note blip synthesised with WebAudio (no asset files). */
export function playBlip() {
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    if (ctx.state === "suspended") void ctx.resume();
    const t0 = ctx.currentTime;
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.12, t0 + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.26);
    [880, 1320].forEach((freq, i) => {
      const osc = ctx!.createOscillator();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t0 + i * 0.07);
      osc.connect(gain);
      osc.start(t0 + i * 0.07);
      osc.stop(t0 + 0.27);
    });
  } catch {
    /* audio unavailable */
  }
}

export function showDesktopNotification({ title, body, tag, onClick }: { title: string; body?: string; tag?: string; onClick?: () => void }) {
  if (notifyPermission() !== "granted") return;
  try {
    const n = new Notification(title, { body, tag, icon: "/logo.svg", silent: true });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
  } catch {
    /* some platforms only allow notifications from a service worker */
  }
}
